// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {clsx} from 'clsx';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';

import {useArtifactDagLazyQuery} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {InstrumentedCenteredWaveLoader as Loader} from '../utility/InstrumentedLoader';
import ArtifactFlowDag from './ArtifactFlowDag';
import {formatArtifactDagQuery, LineageQueryType} from './utils';

export const DAG_REQUEST_LIMIT = 1000;
export const DEFAULT_CLUSTER_SIZE = 5;

type ArtifactDagProps = {
  entityName: string;
  projectName: string;
  artifactTypeName: string;
  artifactCollectionName: string;
  artifactCommitHash: string;
  artifactVersionID: string;
};

type LineageQueryVariableType = {
  artifactVersionID: string;
  limit: number;
  filterGeneratedArtifacts: boolean;
  clusterSize: number | undefined;
  directLineage: boolean;
};

const graphView = {
  direct: {label: 'Direct lineage view', value: 'direct'},
  complete: {label: 'Complete graph view', value: 'complete'},
};

const ArtifactDag = (props: ArtifactDagProps) => {
  const {
    entityName,
    projectName,
    artifactTypeName,
    artifactCollectionName,
    artifactCommitHash,
    artifactVersionID,
  } = props;
  const artifactID = `${artifactCollectionName}:${artifactCommitHash}`;
  const fullArtifactID = `${entityName}/${projectName}/${artifactTypeName}/${artifactID}`;
  const [expandGraph, setExpandGraph] = useState(false);

  const [directLineageToggle, setDirectLineageToggle] = useState(true);

  const [artifactDagQueryVariables, setArtifactDagQueryVariables] =
    useState<LineageQueryVariableType>({
      artifactVersionID,
      limit: DAG_REQUEST_LIMIT,
      filterGeneratedArtifacts: true,
      clusterSize: DEFAULT_CLUSTER_SIZE,
      directLineage: true,
    });

  const [getArtifactDag, {loading, data: latestDagQuery}] =
    useArtifactDagLazyQuery({
      variables: artifactDagQueryVariables,
      context: propagateErrorsContext(),
    });

  const artifactDagQueryCallback = useCallback(
    async (
      loadWithClusters: boolean,
      filterGeneratedArtifacts: boolean,
      directLineage: boolean
    ) => {
      const updatedVariables = {
        ...artifactDagQueryVariables,
        filterGeneratedArtifacts,
        directLineage,
        clusterSize: loadWithClusters ? DEFAULT_CLUSTER_SIZE : undefined,
      };
      await getArtifactDag({
        variables: updatedVariables,
      });
      setArtifactDagQueryVariables(updatedVariables);
    },
    [artifactDagQueryVariables, getArtifactDag]
  );

  useEffect(() => {
    getArtifactDag({
      variables: artifactDagQueryVariables,
    });
    // for initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loader name="artifact-dag-lineage" size="huge" />;
  }
  const {dag} = formatArtifactDagQuery(
    latestDagQuery,
    LineageQueryType.ARTIFACT
  );

  return (
    <Tailwind style={{width: '100%', height: '100%'}}>
      <div data-name="artifact-dag" className="m-[-16px] h-full bg-moon-50">
        <div className="flex items-center justify-between bg-moon-50 pl-[20px] pr-[21px] pt-[12px]">
          <div className="flex items-center gap-[20px]">
            <Select
              options={[graphView.complete, graphView.direct]}
              defaultValue={
                directLineageToggle ? graphView.direct : graphView.complete
              }
              onChange={selected => {
                if (selected != null && selected.value) {
                  artifactDagQueryCallback(
                    !!artifactDagQueryVariables.clusterSize,
                    artifactDagQueryVariables.filterGeneratedArtifacts,
                    selected && selected.value === 'direct'
                  );
                  setDirectLineageToggle(
                    selected && selected.value === 'direct'
                  );
                }
              }}
              // Set to 5 as the dropdown for select component is set to 4
              className="z-[5] w-[240px]"
            />

            <div className="flex items-center gap-[8px]">
              <Switch.Root
                id="cluster-toggle"
                className="h-[24px] w-[40px]"
                checked={expandGraph}
                onCheckedChange={isOn => {
                  artifactDagQueryCallback(
                    !isOn,
                    artifactDagQueryVariables.filterGeneratedArtifacts,
                    artifactDagQueryVariables.directLineage
                  );
                  setExpandGraph(!!isOn);
                }}>
                <Switch.Thumb
                  className={clsx('h-[22px] w-[22px]', {
                    'translate-x-[16px]': expandGraph,
                  })}
                  checked={expandGraph}
                />
              </Switch.Root>
              <Tooltip
                content={'Reduce load time by grouping items into clusters'}
                position="bottom center"
                trigger={
                  <label htmlFor="cluster-toggle">Expanded graph</label>
                }></Tooltip>
            </div>

            <div className="flex items-center gap-[8px]">
              <Switch.Root
                id="include-generated-toggle"
                className="h-[24px] w-[40px]"
                checked={!artifactDagQueryVariables.filterGeneratedArtifacts}
                onCheckedChange={isOn => {
                  artifactDagQueryCallback(
                    !!artifactDagQueryVariables.clusterSize,
                    !isOn,
                    artifactDagQueryVariables.directLineage
                  );
                }}>
                <Switch.Thumb
                  className={clsx('h-[22px] w-[22px]', {
                    'translate-x-[16px]':
                      !artifactDagQueryVariables.filterGeneratedArtifacts,
                  })}
                  checked={!artifactDagQueryVariables.filterGeneratedArtifacts}
                />
              </Switch.Root>
              <label htmlFor="include-generated-toggle">
                Include generated artifacts
              </label>
            </div>
          </div>
          {(expandGraph || Object.entries(dag.clusters).length > 0) && (
            <div className="night-aware min-h-22 flex max-h-22 w-fit items-center rounded-2xl bg-moon-300/50 pl-4 pr-7 text-[14px]">
              <Icon className="mr-4 h-14 w-14" name="warning" />
              <span>
                {expandGraph
                  ? 'Loading without clusters can lead to slow loading times'
                  : 'Some nodes are concealed in this view - Break out items to reveal more.'}
              </span>
            </div>
          )}
        </div>
        {!loading && latestDagQuery != null && (
          <ArtifactFlowDag
            dag={dag}
            artifactID={fullArtifactID}
            filterGeneratedArtifacts={
              artifactDagQueryVariables.filterGeneratedArtifacts
            }
            clusterSize={artifactDagQueryVariables.clusterSize}
            directLineage={artifactDagQueryVariables.directLineage}
          />
        )}
      </div>
    </Tailwind>
  );
};

export default ArtifactDag;
